import React, { useState, useEffect, useCallback } from 'react';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Megaphone, Send } from 'lucide-react';
import { toast } from '../../lib/safeToast';

export default function BackOfficeNotificationsBroadcast() {
  const { api } = useAuth();
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [audience, setAudience] = useState('all');
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get('/backoffice/broadcasts');
      setHistory(res.data?.data || []);
    } catch { setHistory([]); } finally { setHistoryLoading(false); }
  }, [api]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!title.trim() || !message.trim()) { toast.error('Title and message are required'); return; }
    setSending(true);
    try {
      await api.post('/backoffice/broadcasts', { title, message, audience });
      toast.success('Broadcast sent');
      setTitle('');
      setMessage('');
      fetchHistory();
    } catch { toast.error('Failed to send broadcast'); } finally { setSending(false); }
  };

  return (
    <BackOfficeLayout>
      <div className="space-y-6">
        <div className="pb-4">
          <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2"><Megaphone className="w-7 h-7 text-[#0B6445]" />Broadcast Message</h1>
          <p className="text-[#0E1B16]/70 mt-2">Send announcements to merchants and platform users.</p>
        </div>

        <Card className="border-[#DCE4E0] shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-base text-[#073B2E]">Compose Broadcast</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleSend} className="space-y-4 max-w-xl">
              <div className="space-y-1.5">
                <Label>Title</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Scheduled maintenance" required />
              </div>
              <div className="space-y-1.5">
                <Label>Message</Label>
                <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Describe the announcement…" className="min-h-[100px]" required />
              </div>
              <div className="space-y-1.5">
                <Label>Audience</Label>
                <Select value={audience} onValueChange={setAudience}>
                  <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="merchants">Merchants Only</SelectItem>
                    <SelectItem value="admins">Admins Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={sending} className="bg-[#0B6445] hover:bg-[#095b3d] text-white">
                <Send className="w-4 h-4 mr-1.5" />{sending ? 'Sending…' : 'Send Broadcast'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-[#DCE4E0] shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-base text-[#073B2E]">Recent Broadcasts</CardTitle></CardHeader>
          <CardContent>
            {historyLoading ? (
              <div className="flex justify-center py-8"><div className="spinner w-6 h-6" /></div>
            ) : history.length === 0 ? (
              <p className="text-center py-8 text-[#0E1B16]/60">No broadcasts sent yet.</p>
            ) : (
              <div className="space-y-3">
                {history.map((b) => (
                  <div key={b.id} className="border border-[#DCE4E0] rounded-lg p-4">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-semibold text-[#073B2E]">{b.title}</h4>
                      <Badge variant="outline" className="text-xs capitalize">{b.audience}</Badge>
                    </div>
                    <p className="text-sm text-[#0E1B16]/70 mb-2">{b.message}</p>
                    <p className="text-xs text-slate-400">Sent by {b.sent_by_name || 'Admin'} &middot; {b.created_at ? new Date(b.created_at).toLocaleString() : ''}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </BackOfficeLayout>
  );
}
